import { TextRange } from 'src/graphql';

/**
 * Regex pattern to match deletion tags in the format [deleted DD MMM YYYY]
 * This matches the backend DateFormats.DAY_MONTH_YEAR format: 'DD MMM YYYY'
 * Examples: [deleted 01 Jan 2024], [deleted 15 Dec 2023]
 *
 * Note: If the backend date format changes, this regex and related tests will need updates
 */
const DELETION_TAG_REGEX = /\[deleted \d{2} \w{3} \d{4}\]/g;

/**
 * Interface representing a deletion tag found in text
 */
interface DeletionTag {
  start: number;
  end: number;
  text: string;
}

/**
 * Finds all deletion tags in the given text
 * @param text The text to search for deletion tags
 * @returns Array of deletion tag positions
 */
function findDeletionTags(text: string): DeletionTag[] {
  const tags: DeletionTag[] = [];
  let match;

  // Reset regex state
  DELETION_TAG_REGEX.lastIndex = 0;

  while ((match = DELETION_TAG_REGEX.exec(text)) !== null) {
    tags.push({
      start: match.index,
      end: match.index + match[0].length,
      text: match[0],
    });
  }

  return tags;
}

/**
 * Checks if a selection range overlaps with any deletion tag
 * @param selection The user's text selection
 * @param tag The deletion tag to check against
 * @returns True if the selection overlaps with the tag
 */
function isSelectionOverlappingTag(
  selection: TextRange,
  tag: DeletionTag,
): boolean {
  const selectionStart = selection.offset;
  const selectionEnd = selection.offset + selection.length;

  // Check if selection overlaps with tag boundaries
  return (
    // Selection encompasses tag
    // Selection ends inside tag
    (selectionStart < tag.end && selectionEnd > tag.start) || // Partial overlap
    (selectionStart >= tag.start && selectionStart < tag.end) || // Selection starts inside tag
    (selectionEnd > tag.start && selectionEnd <= tag.end) ||
    (selectionStart <= tag.start && selectionEnd >= tag.end)
  );
}

/**
 * Adjusts a text selection to avoid overlapping with deletion tags
 * If the selection spans across deletion tags, it splits into multiple ranges
 * @param text The full text content
 * @param selection The original selection range
 * @returns Array of adjusted selection ranges that don't overlap with deletion tags, or empty array if no valid selections
 */
export function adjustSelectionAroundDeletionTags(
  text: string,
  selection: TextRange,
): TextRange[] {
  if (!text || selection.length === 0) {
    return [selection];
  }

  const deletionTags = findDeletionTags(text);

  if (deletionTags.length === 0) {
    return [selection];
  }

  const selectionStart = selection.offset;
  const selectionEnd = selection.offset + selection.length;

  // Find tags that intersect with the selection
  const intersectingTags = deletionTags.filter((tag) =>
    isSelectionOverlappingTag(selection, tag),
  );

  if (intersectingTags.length === 0) {
    return [selection]; // No overlap, return original selection
  }

  // Sort intersecting tags by their start position
  intersectingTags.sort((a, b) => a.start - b.start);

  const resultRanges: TextRange[] = [];

  // Handle text before the first intersecting tag
  const firstTag = intersectingTags[0];
  if (selectionStart < firstTag.start) {
    const beforeTagLength = firstTag.start - selectionStart;
    if (beforeTagLength > 0) {
      resultRanges.push({
        offset: selectionStart,
        length: beforeTagLength,
      });
    }
  }

  // Handle text between intersecting tags
  for (let i = 0; i < intersectingTags.length - 1; i++) {
    const currentTag = intersectingTags[i];
    const nextTag = intersectingTags[i + 1];

    const gapStart = currentTag.end;
    const gapEnd = nextTag.start;

    // Only include gap if it's within the original selection and has content
    if (
      gapStart < selectionEnd &&
      gapEnd > selectionStart &&
      gapStart < gapEnd
    ) {
      const effectiveStart = Math.max(gapStart, selectionStart);
      const effectiveEnd = Math.min(gapEnd, selectionEnd);

      if (effectiveEnd > effectiveStart) {
        resultRanges.push({
          offset: effectiveStart,
          length: effectiveEnd - effectiveStart,
        });
      }
    }
  }

  // Handle text after the last intersecting tag
  const lastTag = intersectingTags[intersectingTags.length - 1];
  if (selectionEnd > lastTag.end) {
    const afterTagStart = lastTag.end;
    const afterTagLength = selectionEnd - afterTagStart;
    if (afterTagLength > 0) {
      resultRanges.push({
        offset: afterTagStart,
        length: afterTagLength,
      });
    }
  }

  // Filter out any ranges that are empty or invalid
  const validRanges = resultRanges.filter(
    (range) =>
      range.length > 0 &&
      range.offset >= 0 &&
      range.offset + range.length <= text.length,
  );

  // Double-check that none of the result ranges overlap with deletion tags
  const safeRanges = validRanges.filter((range) => {
    return !deletionTags.some((tag) => isSelectionOverlappingTag(range, tag));
  });

  return safeRanges;
}
